import { mapValues, debounce } from "lodash";
import { useNode, useEditor } from "@craftjs/core";
import { getRandomId as getRandomNodeId } from "@craftjs/utils";
import React, { useMemo, useEffect, useRef, useContext } from "react";
import {
  ColorInput,
  SelectInput,
  UnitInput,
  BoxShadowInput,
  MarginInput,
  Panel,
  Tabs,
  BorderRadiusInput,
  BorderInput,
  Spacer,
  RangeNumberInput,
  TabsV2,
  PaddingInput,
} from "../../input";
import { Column } from "../column";
import { GrigoraDashboardContext } from "../../dashboard/context";
import { supportLibrary } from "../../block-supports";
import { changeTransition, isUpdatedFromDefault } from "../../utils";

const toWidthPrecision = (value) => {
  const unitlessValue = parseFloat(value);
  return Number.isFinite(unitlessValue)
    ? parseFloat(unitlessValue.toFixed(2))
    : undefined;
};

function getEffectiveColumnWidth(block, totalBlockCount) {
  const { width = 100 / totalBlockCount } = block.data.props;
  return toWidthPrecision(width);
}

export const ColumnsSettings = () => {
  const {
    id,
    actions: { setProp },
    props,
    dom,
    supports,
  } = useNode((node) => ({
    id: node.id,
    text: node.data.props.text,
    props: node.data.props,
    dom: node.dom,
    supports: node.data?.type?.craft?.related?.supports,
  }));

  const {
    query: { node, parseFreshNode },
    actions: { add, delete: deleteCraftJSNode, setProp: setPropGlobal },
  } = useEditor();

  const parents = node(id).ancestors();

  const updating = useRef(false);

  const {
    columnsCount = node(id).childNodes().length,
    blockGap = "1rem",
    blockGapTablet,
    blockGapMobile,
    textColor,
    backgroundColor,
    typoSize,
    typoLineHeight,
    typoLetterSpacing,
    typoWordSpacing,
    typoWeight,
    typoTransform,
    typoDecoration,
    typoStyle,
    border,
    borderRadius,
    layoutPadding,
    width,
    maxWidth,
    minWidth,
    alignItems,
    tabletColumnsCount = 1,
    mobileColumnsCount = 1,
    boxShadow,
    hoverBorder,
    hoverBorderRadius,
    hoverTextColor,
    hoverBackgroundColor,
    transition,
  } = props;

  function hasExplicitPercentColumnWidths(blocks) {
    return blocks.every((block) => {
      const blockWidth = node(block).get()?.data?.props?.width;
      return Number.isFinite(
        blockWidth?.endsWith?.("%") ? parseFloat(blockWidth) : blockWidth,
      );
    });
  }

  function getColumnWidths(blocks, totalBlockCount = blocks.length) {
    return blocks.reduce((accumulator, block) => {
      const width = getEffectiveColumnWidth(node(block).get(), totalBlockCount);
      return Object.assign(accumulator, { [block]: width });
    }, {});
  }

  function getTotalColumnsWidth(blocks, totalBlockCount = blocks.length) {
    return blocks.reduce(
      (sum, block) =>
        sum + getEffectiveColumnWidth(node(block).get(), totalBlockCount),
      0,
    );
  }

  function getRedistributedColumnWidths(
    blocks,
    availableWidth,
    totalBlockCount = blocks.length,
  ) {
    const totalWidth = getTotalColumnsWidth(blocks, totalBlockCount);

    return mapValues(getColumnWidths(blocks, totalBlockCount), (width) => {
      const newWidth = (availableWidth * width) / totalWidth;
      return toWidthPrecision(newWidth);
    });
  }

  // Option B: useMemo() stores the debounced callback
  const debouncedChangeHandler = useMemo(
    () =>
      debounce((val) => {
        updateColumns(val);
      }, 0),
    [],
  );

  // Stop the invocation of the debounced function
  // after unmounting
  useEffect(() => {
    return () => {
      debouncedChangeHandler.cancel();
    };
  }, []);

  const updateColumns = (val) => {
    // if(updating.current){
    //   return;
    // }

    const childrens = node(id).childNodes();
    // updating.current = true;
    val = Number(val);

    const currentCount = Number(childrens.length);

    var currentChildrens = [...childrens];
    const hasExplicitWidths = hasExplicitPercentColumnWidths(currentChildrens);
    const isAddingColumn = val > currentCount;
    if (isAddingColumn && hasExplicitWidths) {
      // If adding a new column, assign width to the new column equal to
      // as if it were `1 / columns` of the total available space.
      const newColumnWidth = toWidthPrecision(100 / val);

      // Redistribute in consideration of pending block insertion as
      // constraining the available working width.
      const widths = getRedistributedColumnWidths(
        currentChildrens,
        100 - newColumnWidth,
      );

      // Update existing column props
      Object.keys(widths).map((columnID) => {
        setPropGlobal(columnID, (props) => {
          props.width = `${newColumnWidth}%`;
        });
      });

      // Add new columns with width props
      Array.from({
        length: val - currentCount,
      }).map((v, i) => {
        const freshNode = {
          // id: `${id}-${currentCount + i + 1}`,
          id: getRandomNodeId(),
          data: {
            type: Column,
            props: {
              width: `${newColumnWidth}%`,
            },
            isCanvas: true,
          },
        };
        const nodeToAdd = parseFreshNode(freshNode).toNode();
        add(nodeToAdd, id);
      });
    } else if (isAddingColumn) {
      // Add columns
      Array.from({
        length: val - currentCount,
      }).map((v, i) => {
        const freshNode = {
          id: getRandomNodeId(),
          data: {
            type: Column,
            props: {},
            isCanvas: true,
          },
        };
        const nodeToAdd = parseFreshNode(freshNode).toNode();
        add(nodeToAdd, id);
      });
    } else {
      // Delete Nodes
      // The removed column will be the last of the inner blocks.
      Array.from({
        length: -(val - currentCount),
      }).map((v, i) => {
        if (childrens[currentCount - i - 1]) {
          deleteCraftJSNode(childrens[currentCount - i - 1]);
        }
      });

      // Reset if the tablet and mobile columns count is greater than new length.
      if (tabletColumnsCount > val) {
        setProp((props) => {
          props.tabletColumnsCount = val;
        });
      }

      if (mobileColumnsCount > val) {
        setProp((props) => {
          props.mobileColumnsCount = val;
        });
      }

      // The removed column will be the last of the inner blocks.
      currentChildrens = currentChildrens.slice(0, val - currentCount);

      if (hasExplicitWidths) {
        // Redistribute as if block is already removed.
        const widths = getRedistributedColumnWidths(currentChildrens, 100);

        Object.keys(widths).map((columnID) => {
          setPropGlobal(columnID, (props) => {
            props.width = `${widths[columnID]}%`;
          });
        });
      }
      // updating.current = false;
    }

    // updating.current = false;
  };

  const ALIGNITEMS = [
    {
      label: "Start",
      value: "start",
    },
    {
      label: "Center",
      value: "center",
    },
    {
      label: "End",
      value: "end",
    },
  ];

  function GeneralSettings() {
    return (
      <div className="g-settings__wrapper">
        <Spacer spacing="1rem">
          <RangeNumberInput
            isResetVisible={false}
            value={columnsCount}
            onChange={(val) => {
              updateColumns(val);
              setProp((props) => {
                props.columnsCount = val;
              });
            }}
            min={2}
            max={8}
            step={1}
            label={"Total Columns"}
          />
          <SelectInput
            isResetVisible={false}
            value={tabletColumnsCount}
            onChange={(val) => {
              setProp((props) => {
                props.tabletColumnsCount = val;
              });
            }}
            options={Array.from({ length: columnsCount }, (_, i) => {
              return {
                label: i + 1,
                value: i + 1,
              };
            })}
            min={1}
            max={4}
            step={1}
            label={"Tablet Columns"}
          />
          <SelectInput
            value={mobileColumnsCount}
            isResetVisible={false}
            onChange={(val) => {
              setProp((props) => {
                props.mobileColumnsCount = val;
              });
            }}
            options={Array.from({ length: columnsCount }, (_, i) => {
              return {
                label: i + 1,
                value: i + 1,
              };
            })}
            min={1}
            max={4}
            step={1}
            label={"Mobile Columns"}
          />
          <UnitInput
            value={blockGap}
            onChange={(val) => {
              setProp((props) => {
                props.blockGap = val;
              });
            }}
            resetValue={"1rem"}
            label={"Columns Gap"}
            isResponsive={true}
            valueTablet={blockGapTablet}
            valueMobile={blockGapMobile}
            setValueTablet={(val) => {
              setProp((props) => {
                props.blockGapTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.blockGapMobile = val;
              });
            }}
          />
          <UnitInput
            auto
            value={width}
            onChange={(val) => {
              setProp((props) => {
                props.width = val;
              });
            }}
            label={"Width"}
          />
          <UnitInput
            value={maxWidth}
            onChange={(val) => {
              setProp((props) => {
                props.maxWidth = val;
              });
            }}
            label={"Max Width"}
          />
          <UnitInput
            value={minWidth}
            onChange={(val) => {
              setProp((props) => {
                props.minWidth = val;
              });
            }}
            label={"Min Width"}
          />
          <SelectInput
            value={alignItems}
            onChange={(val) => {
              setProp((props) => {
                props.alignItems = val;
              });
            }}
            options={ALIGNITEMS}
            label="Vertical Align"
          />
        </Spacer>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: typoSize },
            { key: typoLineHeight },
            { key: typoLetterSpacing },
            { key: typoWordSpacing },
            { key: typoWeight },
            { key: typoTransform },
            { key: typoDecoration },
            { key: typoStyle },
          ])}
          id={`block-setting-${id}-typography`}
          title={"Typography"}
          initialOpen={false}
        >
          <UnitInput
            value={typoSize}
            onChange={(val) => {
              setProp((props) => {
                props.typoSize = val;
              });
            }}
            label={"Size"}
          />
          <UnitInput
            value={typoLineHeight}
            onChange={(val) => {
              setProp((props) => {
                props.typoLineHeight = val;
              });
            }}
            label={"Line Height"}
          />
          <UnitInput
            value={typoLetterSpacing}
            onChange={(val) => {
              setProp((props) => {
                props.typoLetterSpacing = val;
              });
            }}
            label={"Letter Spacing"}
          />
          <UnitInput
            value={typoWordSpacing}
            onChange={(val) => {
              setProp((props) => {
                props.typoWordSpacing = val;
              });
            }}
            label={"Word Spacing"}
          />
          <SelectInput
            value={typoWeight}
            onChange={(val) => {
              setProp((props) => {
                props.typoWeight = val;
              });
            }}
            options={[
              { label: "100", value: "100" },
              { label: "200", value: "200" },
              { label: "300", value: "300" },
              { label: "400", value: "400" },
              { label: "500", value: "500" },
              { label: "600", value: "600" },
              { label: "700", value: "700" },
              { label: "800", value: "800" },
              { label: "900", value: "900" },
            ]}
            label="Weight"
          />
          <SelectInput
            value={typoTransform}
            onChange={(val) => {
              setProp((props) => {
                props.typoTransform = val;
              });
            }}
            options={[
              { label: "Uppercase", value: "uppercase" },
              { label: "Lowercase", value: "lowercase" },
              { label: "Capitalize", value: "capitalize" },
              { label: "None", value: "none" },
            ]}
            label="Transform"
          />
          <SelectInput
            value={typoDecoration}
            onChange={(val) => {
              setProp((props) => {
                props.typoDecoration = val;
              });
            }}
            options={[
              { label: "Underline", value: "underline" },
              { label: "Overline", value: "overline" },
              { label: "None", value: "none" },
            ]}
            label="Decoration"
          />
          <SelectInput
            value={typoStyle}
            onChange={(val) => {
              setProp((props) => {
                props.typoStyle = val;
              });
            }}
            options={[
              {
                label: "Normal",
                value: "normal",
              },
              {
                label: "Italic",
                value: "italic",
              },
            ]}
            label="Style"
          />
        </Panel>
      </div>
    );
  }

  function StyleSettings() {
    const colorTabs = [
      {
        label: "Normal",
        key: "normal",
        children: handleColorStates({
          textColorPropKey: "textColor",
          textColorProp: textColor,
          backgroundColorPropKey: "backgroundColor",
          backgroundColorProp: backgroundColor,
        }),
      },
      {
        label: "Hover",
        key: "hover",
        children: handleColorStates({
          textColorPropKey: "hoverTextColor",
          textColorProp: hoverTextColor,
          backgroundColorPropKey: "hoverBackgroundColor",
          backgroundColorProp: hoverBackgroundColor,
        }),
      },
    ];

    function handleColorStates({
      textColorPropKey,
      textColorProp,
      backgroundColorPropKey,
      backgroundColorProp,
    }) {
      return (
        <>
          <ColorInput
            resetValue={undefined}
            value={textColorProp}
            onChange={(val) => {
              setProp((props) => {
                props[textColorPropKey] = val;
              });
            }}
            label={"Text"}
          />
          <ColorInput
            resetValue={undefined}
            value={backgroundColorProp}
            onChange={(val) => {
              setProp((props) => {
                props[backgroundColorPropKey] = val;
              });
            }}
            label={"Background"}
          />
          {(textColorPropKey === "hoverTextColor" ||
            backgroundColorPropKey === "hoverBackgroundColor") && (
            <RangeNumberInput
              min={0}
              max={10}
              step={0.05}
              value={
                transition?.types?.[("color", "background-color")]?.duration
              }
              onChange={(val) => {
                changeTransition(
                  ["color", "background-color"],
                  { duration: val },
                  transition,
                  setProp,
                );
              }}
              label={"Transition Time"}
            />
          )}
        </>
      );
    }
    return (
      <>
        <div className="g-settings__wrapper">
          <Panel
            isUpdated={isUpdatedFromDefault([
              { key: textColor },
              { key: backgroundColor },
              { key: hoverTextColor },
              { key: hoverBackgroundColor },
            ])}
            id={`block-setting-${id}-colors`}
            title={"Colors"}
            initialOpen={true}
          >
            <TabsV2 items={colorTabs} />
          </Panel>
        </div>
      </>
    );
  }

  function AdvancedSettings() {
    const borderRadiusTabs = [
      {
        label: "Normal",
        key: "normal",
        children: handleBorderRadiusStates({
          borderPropKey: "border",
          borderProp: border,
          borderRadiusPropKey: "borderRadius",
          borderRadiusProp: borderRadius,
        }),
      },
      {
        label: "Hover",
        key: "hover",
        children: handleBorderRadiusStates({
          borderPropKey: "hoverBorder",
          borderProp: hoverBorder,
          borderRadiusPropKey: "hoverBorderRadius",
          borderRadiusProp: hoverBorderRadius,
        }),
      },
    ];

    function handleBorderRadiusStates({
      borderPropKey,
      borderProp,
      borderRadiusPropKey,
      borderRadiusProp,
    }) {
      return (
        <>
          <BorderInput
            value={borderProp}
            onChange={(val) => {
              setProp((props) => {
                props[borderPropKey] = val;
              });
            }}
            label={"Border"}
            isResponsive={true}
          />
          <BorderRadiusInput
            resetValue={undefined}
            value={borderRadiusProp}
            onChange={(val) => {
              setProp((props) => {
                props[borderRadiusPropKey] = val;
              });
            }}
            label={"Border Radius"}
          />
          {(borderRadiusPropKey === "hoverBorderRadius" ||
            borderPropKey === "hoverBorder") && (
            <RangeNumberInput
              min={0}
              max={10}
              step={0.05}
              value={transition?.types?.[("border", "border-radius")]?.duration}
              onChange={(val) => {
                changeTransition(
                  ["border", "border-radius"],
                  { duration: val },
                  transition,
                  setProp,
                );
              }}
              label={"Transition Time"}
            />
          )}
        </>
      );
    }

    return (
      <>
        <Panel
          isUpdated={isUpdatedFromDefault([{ key: layoutPadding }])}
          id={`block-setting-${id}-layout`}
          title={"Layout"}
          initialOpen={true}
        >
          <PaddingInput
            value={layoutPadding}
            onChange={(val) => {
              setProp((props) => {
                props.layoutPadding = val;
              });
            }}
            label={"Padding"}
          />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: border },
            { key: borderRadius },
            { key: hoverBorder },
            { key: hoverBorderRadius },
          ])}
          id={`block-setting-${id}-border`}
          title={"Border"}
          initialOpen={false}
        >
          <TabsV2 items={borderRadiusTabs} />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([{ key: boxShadow }])}
          id={`block-setting-${id}-box-shadow`}
          title={"Shadows"}
          initialOpen={false}
        >
          <BoxShadowInput
            resetValue={undefined}
            value={boxShadow}
            onChange={(val) => {
              setProp((props) => {
                props.boxShadow = val;
              });
            }}
          />
        </Panel>
        {supports &&
          supports.map((support) => {
            var Component = supportLibrary[support]?.component;

            if (!Component) {
              return;
            }

            return (
              <>
                <Component
                  block={{
                    id,
                    props,
                    dom,
                  }}
                  actions={{
                    setProp,
                  }}
                ></Component>
              </>
            );
          })}
      </>
    );
  }
  const tabs = [
    {
      label: "General",
      key: "general",
      children: GeneralSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: columnsCount },
        { key: tabletColumnsCount },
        { key: mobileColumnsCount },
        { key: blockGap },
        { key: blockGapTablet },
        { key: blockGapMobile },
        { key: width },
        { key: maxWidth },
        { key: minWidth },
        { key: alignItems },
      ]),
    },
    {
      label: "Style",
      key: "style",
      children: StyleSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: textColor },
        { key: backgroundColor },
        { key: typoSize },
        { key: typoLineHeight },
        { key: typoLetterSpacing },
        { key: typoWordSpacing },
        { key: typoWeight },
        { key: typoTransform },
        { key: typoDecoration },
        { key: typoStyle },
        { key: hoverTextColor },
        { key: hoverBackgroundColor },
      ]),
    },
    {
      label: "Advanced",
      key: "advanced",
      children: AdvancedSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: layoutPadding },
        { key: layoutPadding },
        { key: boxShadow },
        { key: border },
        { key: borderRadius },
        { key: hoverBorder },
        { key: hoverBorderRadius },
      ]),
    },
  ];

  return (
    <div>
      <Tabs
        id={`block-settings-tab-${id}`}
        type="block-setting"
        items={tabs.map((item, i) => {
          return {
            label: item.label,
            key: item.key,
            children: item.children,
          };
        })}
      />
    </div>
  );
};
